import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Upload, DollarSign, Image, CheckCircle, Edit, User } from 'lucide-react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useArtworkCount } from '@/features/artworks/hooks/useArtworks'
import { ArtworkStatus } from '@/types/entities'
import { formatUserName } from '@/lib/utils'
import { ProfileWelcomeCard } from '@/features/users/components/ProfileWelcomeCard'
import { BiographyReadyBanner } from '@/features/easel/components/BiographyReadyBanner'

interface StatCardProps {
  title: string
  value: number
  icon: React.ReactNode
}

function StatCard({ title, value, icon }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-medium font-sans text-muted-foreground">
          {title}
        </CardTitle>
        <div className="text-muted-foreground/70">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-4xl font-serif font-semibold tracking-tight">{value}</div>
      </CardContent>
    </Card>
  )
}

export function ArtistDashboard() {
  const { user } = useAuth()
  const { data: totalArtworks } = useArtworkCount()
  const { data: publishedCount } = useArtworkCount(ArtworkStatus.PUBLISHED)
  const { data: draftCount } = useArtworkCount(ArtworkStatus.DRAFT)

  if (!user) return null

  return (
    <div className="space-y-8 p-8">
      <div className="space-y-1">
        <h1 className="font-serif text-4xl font-semibold tracking-tight">
          Welcome back, {formatUserName(user)}!
        </h1>
        <p className="text-muted-foreground text-base">Here's what's happening with your art</p>
      </div>

      {/* A drafted biography nobody has been told about. Above the profile card because it is
          the one thing on this screen with something waiting on the artist. */}
      <BiographyReadyBanner artistId={user.id} />

      {/* Profile Welcome Card */}
      <ProfileWelcomeCard user={user} />

      {/* Stats Grid */}
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Artworks"
          value={totalArtworks || 0}
          icon={<Image className="h-5 w-5" />}
        />
        <StatCard
          title="Published"
          value={publishedCount || 0}
          icon={<CheckCircle className="h-5 w-5" />}
        />
        <StatCard
          title="Draft"
          value={draftCount || 0}
          icon={<Edit className="h-5 w-5" />}
        />
        <StatCard
          title="Total Sales"
          value={0}
          icon={<DollarSign className="h-5 w-5" />}
        />
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Button asChild size="lg" className="h-24 text-base shadow-subtle">
              <Link to="/app/upload">
                <Upload className="mr-2 h-5 w-5" />
                Upload New Art
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-24 text-base shadow-subtle">
              <Link to="/app/sales">
                <DollarSign className="mr-2 h-5 w-5" />
                View My Sales
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-24 text-base shadow-subtle">
              <Link to="/app/account">
                <User className="mr-2 h-5 w-5" />
                My Profile
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

